import { Loadable, Loaded, LoadError, Loading, LoadingState } from 'LoadingState';
import { ODataTable } from 'odata/ODataTable';
import { failError, Failure, Fetcher } from 'query/Fetch';
import Query from 'query/Query';
import { QueryColumn } from 'query/QueryColumn';
import React, { CSSProperties, FocusEventHandler, KeyboardEventHandler, MouseEventHandler, ReactElement } from 'react';
import { Cell, CellType } from 'table/Cell';
import { CollectionTable } from 'table/CollectionTable';
import { Row } from 'table/Row';
import Table from 'table/Table';
import { ContextMenuRender } from './ContextMenu';
import './DataTable.css';
import { FetchError } from './FetchError';
import { FilterPane } from './FilterPane';
import { RowHeader } from './RowHeader';
import { Spinner } from './Spinner';
import { Action, ActionTriggeredListener } from 'actions/Action';
import { ProgressNotifier } from 'actions/ProgressNotifier';
import MultipleQueryColumn, { OrderedBy } from 'query/MultipleQueryColumn';
import { ColumnType } from 'query/ColumnType';
import TableColumn from 'table/TableColumn';
import { expandRows } from 'expanderIterator/ExpandRows';

// TODO If the size of the contents div changes:
//  https://www.pluralsight.com/guides/re-render-react-component-on-window-resize

type EnumDictionary<T extends string | symbol | number, U> = {
    [K in T]: U;
};

export interface DataTableProps {
    query: Query; // This gets mutated. This is bad practise and I'd like to hear of a better way of doing this.
    menu: Action[];
    fetcher: Fetcher;
}

export interface DataTableState {
    visibleRows: Loadable<Row[]>;
    count: number; // Total number of columns in this row.
    firstVisibleRow: number; // Index of the row at the top of the visible table.  
    dropColumnMarkerPosition: number | null;
    resizingColumn?: QueryColumn; // null, or which column we're resizing.
    columnScroll: number; // How many pixels we have scrolled horizontally.
    queryVersion: number; // The (previous) version of the query, to detect changes.
    rowHeaderContextMenuVisible: boolean; // whether the row header's context menu is visible.
    cellContextMenuVisible: boolean; // whether the context menu is visible.
    selectColumnMenuVisible: boolean; // sub-menu from rowHeaderContextMenu
    contextMenuTop: number; // coordinates of the context menu.
    contextMenuLeft: number; // coordinates of the context menu.
    rowHeaderSelectedColumn?: QueryColumn; // which column the row header context menu is on.
    selectedCell?: Cell; // Which is the currently selected cell.

    /*
    selectedCell: CellSelection; 
    selectedRows: 
    selectedColumns:
    */
}

export class DataTable
    extends React.Component<DataTableProps, DataTableState>
    implements ActionTriggeredListener {

    // A mechanism to get to the DOM to get the content div height.
    private contentDivRef: React.RefObject<HTMLDivElement>;

    // Used to avoid lag when scrolling headers horizontally.
    private headerDivRef: React.RefObject<HTMLDivElement>;

    // The cell with keyboard focus
    private focusRef?: React.RefObject<HTMLDivElement>;

    // Managing virtual scrolling:
    // TODO: Size up text and then actually read size from the DOM.
    private _pixelsPerRow: number = 24;
    private numVisibleRows: number = 3; // Should be height/pixelsPerRow
    // TODO: does numVisibleRows update when the window is resized???

    private focusRow?: number;
    private focusColumn?: number;

    constructor(props: Readonly<DataTableProps>) {
        super(props);

        this.state = {
            visibleRows: new Loading(),
            count: 10,
            firstVisibleRow: 0,
            dropColumnMarkerPosition: null,
            resizingColumn: undefined,
            columnScroll: 0,
            queryVersion: 0,
            rowHeaderContextMenuVisible: false,
            cellContextMenuVisible: false,
            selectColumnMenuVisible: false,
            contextMenuTop: 0,
            contextMenuLeft: 0,
            selectedCell: undefined
        };
        this.contentDivRef = React.createRef(); // Get the content div height.
        this.headerDivRef = React.createRef(); // Avoid lag when scrolling headers horizontally.
        this.focusRef = React.createRef();

        this.onOrderBy = this.onOrderBy.bind(this);
        this.renderHeadings = this.renderHeadings.bind(this);
        this.renderTableContent = this.renderTableContent.bind(this);

        for (let each of this.props.menu) {
            each.addActionTriggeredObserver(this);
        }
    }
    actionWasTriggered(a: Action): void {
        this.columnsChanged();
    }

    public static defaultProps = {
        table: (new CollectionTable([], [])),
        refetch: (() => { })
    }

    render = () => {
        let columnWidths = (
            this.props.query.expandedColumns
                .map((each) => each.pixelWidth));

        let gridStyle = this.gridStyle(columnWidths);

        let tableWidth = columnWidths.reduce((a, v) => a + v, 0);

        return (
            <div className="datatable"
                onMouseMove={this.onMouseMove}
                onMouseUp={this.onMouseUp}
                onMouseLeave={this.onMouseUp}
                onDrop={this.onHeadingDrop}
                onDragOver={this.onHeadingDragOver}>

                <FilterPane query={this.props.query} onChanged={this.onFilterChanged} />

                {/* The column headings.
                Note that using translate will mean that any fixed positions will now be relative to me.
                In particular, the context menus. */}
                <div
                    className="datatable-headerdiv"
                    style={{
                        ...gridStyle,
                        width: tableWidth,
                        transform: `translateX(${-this.state.columnScroll}px)`
                    }}
                    ref={this.headerDivRef}>
                    {this.renderDropColumnMarker()}
                    {this.renderHeadings()}
                </div>

                {/* The scroll bar. */}
                <div
                    className="datatable-contentsdiv"
                    onScroll={(e) => this.onScroll(e)}
                    ref={this.contentDivRef}>

                    {/* The enormous div to make the scroll bar happen. */}
                    <div style={{ height: this._pixelsPerRow * this.expandedRowsCount() }}>

                        {/* Only the currently visible rows. */}
                        <div style={{
                            ...gridStyle,
                            transform: `translateY(${this.state.firstVisibleRow * this._pixelsPerRow}px)`
                        }}>
                            {this.renderTableContentLoadable()}
                        </div>
                    </div>
                </div>

                {/* Context menus. Read it's class description to see why it's not a <ContextMenu>. */}
                <ContextMenuRender
                    target={undefined}
                    menu={() => { return this.rowHeaderContextMenu(this.state.rowHeaderSelectedColumn) }}
                    left={this.state.contextMenuLeft}
                    top={this.state.contextMenuTop}
                    visible={this.state.rowHeaderContextMenuVisible} />
                {/* Cell context menu */}
                <ContextMenuRender
                    target={this.state.selectedCell}
                    menu={this.cellContextMenu}
                    left={this.state.contextMenuLeft}
                    top={this.state.contextMenuTop}
                    visible={this.state.cellContextMenuVisible} />
                <ContextMenuRender
                    target={undefined}
                    menu={this.selectColumnMenu}
                    left={this.state.contextMenuLeft}
                    top={this.state.contextMenuTop}
                    visible={this.state.selectColumnMenuVisible} />
                {/* This lets the user click-away a context menu. It's invisible and covers the whole page. */}
                <div className={this.state.rowHeaderContextMenuVisible || this.state.cellContextMenuVisible
                    ? 'contextMenuRestOfPage' : 'hidden'}
                    onClick={(ev) => this.dismissContextMenu()}
                    onContextMenu={(ev) => this.dismissContextMenu()}
                >
                </div>
            </div>
        );
    }

    private dismissContextMenu = () => {
        this.setState(
            {
                rowHeaderContextMenuVisible: false,
                cellContextMenuVisible: false,
                selectColumnMenuVisible: false
            });
    }

    private gridStyle = (columnWidths: Array<number>) => {
        let c =
            columnWidths
                .map((each) => `${each}px `)
                .reduce((a, v) => a.concat(v), "");

        return {
            display: 'grid',
            gridTemplateColumns: c,
        };
    }

    private expandedRowsCount() {
        let depth = this.props.query.depth();
        if (depth > 1) {
            // We overestimate the number of rows because we have no way of knowing 
            // how many expanded rows there are in total, unless we manually count them 
            // ourselves, which won't scale.
            return this.state.count * Table.MAX_EXPANDED_ROWS * (depth - 1);
        } else {
            return this.state.count;
        }
    }

    /* Render the column headings.
       They could be turned into a seperate component, but it's
       actually simpler to manage having all this in the same
       component... for now. */
    private renderHeadings(): JSX.Element {
        let rootColumn: MultipleQueryColumn = this.props.query;
        if (rootColumn.isEmpty()) {
            return <></>;
        }

        let laidOutColumns: ColumnsLaidOut = ColumnsLaidOut.fromColumnDefinition(rootColumn);

        return <>{
            laidOutColumns.map(each => {
                let layout: CSSProperties = {
                    gridRowStart: each.row + 1,
                    gridRowEnd: each.row + 2,
                    gridColumnStart: each.columnStart + 1,
                    gridColumnEnd: each.columnEnd + 2,
                    position: "relative"
                };

                let key = `G${each.row}|${each.columnStart}|${each.columnEnd}`

                return <RowHeader
                    column={each.columnDefinition}
                    layout={layout}
                    key={key}
                    orderedBy={orderedBy(this.props.query, each.columnDefinition)}
                    onOrderBy={this.onOrderBy}
                    startResizeColumn={this.onMouseDown}
                    columnsChanged={this.columnsChanged}
                    onContextMenu={this.onRowHeaderContextMenu}
                />
            })
        }</>;
    }

    private renderTableContentLoadable(): JSX.Element | JSX.Element[] {
        let rows = this.state.visibleRows;
        let inner;

        switch (rows.getState()) {
            case LoadingState.Loading:
                inner = <Spinner />
                break;
            case LoadingState.Loaded:
                inner = this.renderTableContent(rows.getValue());
                break;
            case LoadingState.Error:
                inner = <FetchError error={rows.getError()} />
                break;
        }

        return inner;
    }

    /** Render only the visible cells. */
    private renderTableContent = (rows: Row[]): JSX.Element[] => {
        let renderedRows: Array<ReactElement> = [];
        let row: Row | null;
        for (let i = 0; i < this.numVisibleRows; i++) {
            if (i >= rows.length) {
                break;
            }
            row = rows[i];
            this.renderRowTo(row, renderedRows, i);
        }
        return renderedRows;

    }

    private renderRowTo = (row: Row, renderToMe: Array<ReactElement>, y: number) => {
        let expandedColumns = this.props.query.expandedColumns;

        if (!row) {
            console.error("No row.");
            renderToMe.push(<>Error: no row.</>);
            return;
        }

        if (!row.cells) {
            console.error("No cells in row.");
            renderToMe.push(<>Error: no cells in row.</>);
            return;
        }


        for (let x = 0; x < row.cells.length; x++) {
            let eachColumn = expandedColumns[x];
            const layout: React.CSSProperties = {
                height: this._pixelsPerRow,
                gridRowStart: y + 1,
                gridRowEnd: y + 2,
                gridColumnStart: x + 1,
                gridColumnEnd: x + 2,
            };

            let cell = this.renderCellContent(eachColumn, row.cells[x], layout, x, y)

            renderToMe.push(cell);
        }
    }

    private static readonly columnTypeToCSSClass: EnumDictionary<ColumnType, string> = {
        [ColumnType.Binary]: 'datatable-cell-other',
        [ColumnType.RootColumn]: 'datatable-cell-other',
        [ColumnType.ParentColumn]: 'datatable-cell-other',
        [ColumnType.Boolean]: 'datatable-cell-string',
        [ColumnType.Byte]: 'datatable-cell-number',
        [ColumnType.Date]: 'datatable-cell-string',
        [ColumnType.DateTimeOffset]: 'datatable-cell-string',
        [ColumnType.Decimal]: 'datatable-cell-number',
        [ColumnType.Double]: 'datatable-cell-number',
        [ColumnType.Duration]: 'datatable-cell-other',
        [ColumnType.Guid]: 'datatable-cell-other',
        [ColumnType.Int16]: 'datatable-cell-number',
        [ColumnType.Int32]: 'datatable-cell-number',
        [ColumnType.Int64]: 'datatable-cell-number',
        [ColumnType.SByte]: 'datatable-cell-number',
        [ColumnType.Single]: 'datatable-cell-number',
        [ColumnType.Stream]: 'datatable-cell-other',
        [ColumnType.String]: 'datatable-cell-string',
        [ColumnType.TimeOfDay]: 'datatable-cell-string',
        [ColumnType.Geography]: 'datatable-cell-other',
        [ColumnType.GeographyPoint]: 'datatable-cell-other',
        [ColumnType.GeographyLineString]: 'datatable-cell-other',
        [ColumnType.GeographyPolygon]: 'datatable-cell-other',
        [ColumnType.GeographyMultiPoint]: 'datatable-cell-other',
        [ColumnType.GeographyMultiLineString]: 'datatable-cell-other',
        [ColumnType.GeographyMultiPolygon]: 'datatable-cell-other',
        [ColumnType.GeographyCollection]: 'datatable-cell-other',
        [ColumnType.Geometry]: 'datatable-cell-other',
        [ColumnType.GeometryPoint]: 'datatable-cell-other',
        [ColumnType.GeometryLineString]: 'datatable-cell-other',
        [ColumnType.GeometryPolygon]: 'datatable-cell-other',
        [ColumnType.GeometryMultiPoint]: 'datatable-cell-other',
        [ColumnType.GeometryMultiLineString]: 'datatable-cell-other',
        [ColumnType.GeometryMultiPolygon]: 'datatable-cell-other',
        [ColumnType.GeometryCollection]: 'datatable-cell-other',

    };

    private renderCellContent: (column: QueryColumn, cell: Cell, layout: React.CSSProperties, x: number, y: number) => ReactElement
        = (column, cell, layout, x, y) => {
            if (!cell) {
                return <div>Render error: not a cell.</div>
            }

            if (cell._type === CellType.EXHAUSTED) {
                return <div style={layout}></div>
            }

            let cellContent: any = cell._value;
            if (undefined === cellContent || null === cellContent) {
                cellContent = "";
            } else if ("object" === typeof cellContent) {
                cellContent = "";
            }

            if (!column) {
                return <div style={layout}>Error: missing cell</div>
            }

            let cssClass = DataTable.columnTypeToCSSClass[column.type];

            let ref = (cell === this.state.selectedCell) ? this.focusRef : null;

            let absoluteY = y + this.state.firstVisibleRow;

            return <div
                style={layout}
                className={'datatable-cell-background'}
                onContextMenu={this.onCellContextMenu}
                onClick={() => this.selectCell(cell)}
                onKeyDown={this.onKeyDown}
                tabIndex={0}
                onFocus={this.onCellFocusIn}
                onBlur={this.onBlur}
                ref={ref}
                id={keyOf(x, absoluteY)}
                key={keyOf(x, absoluteY)}
                data-row={absoluteY}
                data-column={x}
            >
                <div className={cssClass} style={{ userSelect: 'none' }}>{cellContent}</div>
            </div>;

        }

    private selectCell = (cell: Cell) => {
        this.setState({ selectedCell: cell });
        // TODO: update the query as well.
    }

    private renderDropColumnMarker(): JSX.Element {
        if (null === this.state.dropColumnMarkerPosition) {
            return <></>
        } else {
            // TODO: centre it without magic number (-6)
            let style: CSSProperties = {
                position: 'absolute',
                marginLeft: `${this.state.dropColumnMarkerPosition - 6}px`,
                zIndex: 20
            }
            return <div className='datatable-columndropmarker' style={style}>
                ◈
            </div>
        }
    }

    private cellContextMenu: () => Array<Action> = () => {
        let result: Array<Action> = [];
        result.push(new CopyURL(this));

        for (let each of this.props.menu) {
            result.push(each);
        }
        return result;
    }

    public onCopyUrl = () => {
        let table = this.props.query.table as ODataTable;
        let url = table.getUrl(this.props.query);
        navigator.clipboard.writeText(url.toString()); // async, returns a promise.
        this.dismissContextMenu();
    }

    /* Callback from ColumnHeaders to order by that column. */
    private onOrderBy(
        column: QueryColumn,
        orderBy: OrderedBy
    )
        : void {
        this.props.query.orderBy(column, orderBy);
        this.forceUpdate();
    }

    /* Callback from columns when there are changes in orderedBy, expand. 
       OPTIMIZATION: This can be split up into multiple event handlers. */
    private columnsChanged = () => {
        // TODO: move the query changed logic into whatever just mutated it.
        this.props.query.renumberColumns();
        this.props.query.changed(); // Makes this.componentDidUpdate() do stuff.
        this.setState(this.state);
    }

    onKeyDown: KeyboardEventHandler<HTMLDivElement> = (ev) => {
        // Whitelist
        switch (ev.key) {
            case 'Tab':
                return;
        }

        ev.preventDefault();
        // TODO: keyboard mapping and Actions.
        switch (ev.key) {
            case 'ArrowUp':
                this.up();
                break;
            case 'ArrowDown':
                this.down();
                break;
            case 'ArrowLeft':
                this.left();
                break;
            case 'ArrowRight':
                this.right();
                break;
            case 'PageUp':
                this.pageUp();
                break;
            case 'PageDown':
                this.pageDown();
                break;
            case "Enter":
                this.down();
                this.leftmost();
                break;
            case 'Home':
                if (ev.ctrlKey === true) {
                    this.topLeftmost();
                } else {
                    this.leftmost();
                }
                break;
            case 'End':
                if (ev.ctrlKey === true) {
                    this.bottomRightmost();
                } else {
                    this.rightmost();
                }
                break;
            default:
                console.log(ev.key);
        }
    }

    up = () => {
        this.withFocusedRow((x, y) => {
            let focused = document.getElementById(keyOf(x, y - 1));
            if (focused) {
                focused.focus();
            } else {
                if (this.focusRow !== undefined) {
                    this.focusRow = Math.max(0, this.focusRow - 1);
                    this.scrollToFocus();
                }
            }
        });
    }

    down = () => {
        this.withFocusedRow((x, y) => {
            let focused = document.getElementById(keyOf(x, y + 1));
            if (focused) {
                focused.focus();
            } else {
                if (this.focusRow !== undefined) {
                    this.focusRow = Math.min(this.state.count - 1, this.focusRow + 1);
                    this.scrollToFocus();
                }
            }
        });
    }

    left = () => {
        this.withFocusedRow((x, y) => {
            document.getElementById(keyOf(x - 1, y))?.focus();
        },)
    }

    right = () => {
        this.withFocusedRow((x, y) => {
            document.getElementById(keyOf(x + 1, y))?.focus();
        },)
    }

    pageUp = () => {
        if (this.focusRow !== undefined) {
            this.focusRow = Math.max(0, this.focusRow - this.numVisibleRows);
            this.scrollToFocus();
        }
    }

    pageDown = () => {
        if (this.focusRow !== undefined) {
            this.focusRow = Math.min(this.state.count - 1, this.focusRow + this.numVisibleRows);
            this.scrollToFocus();
        }
    }

    topLeftmost = () => {
        let leftmost = document.getElementById(keyOf(0, 0));
        if (leftmost) {
            leftmost.focus();
        } else {
            this.focusRow = 0;
            this.focusColumn = 0;
            this.scrollToFocus();
        }
    }

    bottomRightmost = () => {
        let lastRow = this.state.count - 1;
        let lastColumn = this.props.query.numColumns() - 1;

        let rightmost = document.getElementById(keyOf(lastColumn, lastRow));
        if (rightmost) {
            rightmost.focus();
        } else {
            this.focusRow = lastRow;
            this.focusColumn = lastColumn;
            this.scrollToFocus();
        }
    }

    leftmost = () => {
        this.withFocusedRow((x, y) => {
            document.getElementById(keyOf(0, y))?.focus();
        },)
    }

    rightmost = () => {
        this.withFocusedRow((x, y) => {
            let lastcolumn = this.props.query.numColumns() - 1;
            document.getElementById(keyOf(lastcolumn, y))?.focus();
        },)
    }


    /* Scrolling behaviour.

    TODO: not implemented yet.

    The scroll bar is in charge. If you change the scroll bar, you will end up with unwanted
    events.

    We do two things with the scroll events:
    1. We try to request the same URLs from positions on the scroll bar, so that
       a CDN in front of the OData server can cache scroll positions.
    2. We don't know the number of rows from an expanded column, so we differentiate between
       jump-scrolling (moving the scroll bar) and incremental scrolling (cursor keys or page up/down).

    CDN optimisation: the scroll bar has only a few hundred pixel positions it can be at. If 
    a user grabs the scroll bar and moves up or down, only a few hundred pages will need to be
    shown to the user. We try hard to make sure that as the user scrolls, for a given query, 
    for different platforms and screen resolutions, the URL requests will be similar enough for
    a CDN to cache. 

    A jump-scroll is where the user
    scrolls such that no previously visible row is now visible on the page. An incremental scroll
    is where visible rows are visible before and after the scroll.  

    We count the pixels and round the number of waypoints down (?) to the nearest power of two. 
    For example, an 810 pixel scroll bar will have 512 waypoints.

    Incremental scrolls are detected when the delta between the old and new scroll positions 
    has some overlap, with special handling for page-up and page-down to ensure the 
    next page aligns with the previous. 

    This has a problem: What happens when a user jump-scrolls to the middle, and then incremental scrolls
    to the top or bottom? A jump-scroll only scrolls to an estimated position, meaning that we can
    overshoot or undershoot the top or the bottom.

    */

    onScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
        // Scroll contents vertically
        let scrollY = (event.target as any).scrollTop;
        let currentTopVisibleRow = Math.floor(scrollY / this._pixelsPerRow);

        // Scroll the headings horizonally.
        let scrollX = (event.target as any).scrollLeft;

        // This reduces lag a little bit, but it's still noticable.
        if (this.headerDivRef.current) {
            this.headerDivRef.current.style.transform = `translateX(${-scrollX}px)`;
        } else {
            console.log("Having trouble scrolling horizontally: ref was invalid");
        }

        this.setState({ firstVisibleRow: currentTopVisibleRow, columnScroll: scrollX });
    }

    onCellFocusIn: FocusEventHandler<HTMLDivElement> = (ev) => {
        // Store the focus in case we redraw (due to scrolling).
        this.withFocusedRow((x, y) => {
            this.focusRow = y;
            this.focusColumn = x;
        },);

    }

    onBlur: FocusEventHandler<HTMLDivElement> = (ev) => {
        this.focusRow = undefined;
        this.focusColumn = undefined;
    }

    private withFocusedRow(
        fn: (x: number, y: number) => void
    ): void {
        let yStr = document?.activeElement?.getAttribute('data-row');
        let xStr = document?.activeElement?.getAttribute('data-column');
        if (xStr && yStr) {
            let x: number = parseInt(xStr);
            let y: number = parseInt(yStr);
            if (typeof x === 'number' && typeof y === 'number') {
                fn(x, y);
                return;
            }
        }
    }


    /* Loading stuff. */

    componentDidMount: () => void = () => {
        this.recalculateNumVisibleRows();
        this.fetchRows();
        this.fetchCount();
    }

    componentDidUpdate = (prevProps: DataTableProps, prevState: DataTableState) => {
        this.recalculateNumVisibleRows();
        this.reSetFocus();

        if (this.props.query !== prevProps.query) {
            // New query selected.
            this.setState({
                visibleRows: new Loading(),
                queryVersion: this.props.query.version,
                firstVisibleRow: 0,
                columnScroll: 0
            });
            this.contentDivRef.current?.scrollTo(0, 0);
            try {
                this.fetchRows();
            } catch (e) {
                console.error(e);
                this.setState({
                    visibleRows: new LoadError(failError(e as Error))
                });
            }
            this.fetchCount();// TODO: this isn't always necessary. E.g. if columns were rearranged.
        } else if (this.props.query.version !== this.state.queryVersion) {
            // Filters changed 
            this.setState({
                visibleRows: new Loading(),
                queryVersion: this.props.query.version
            });
            try {
                this.fetchRows();
            } catch (e) {
                console.error(e);
                this.setState({
                    visibleRows: new LoadError(failError(e as Error))
                });
            }
            this.fetchCount();// TODO: this isn't always necessary. E.g. if columns were rearranged.
        } else if (this.state.firstVisibleRow !== prevState.firstVisibleRow) {
            // Table was scrolled.
            this.setState({ visibleRows: new Loading() });
            this.fetchRows();
        }
    }

    private recalculateNumVisibleRows = () => {
        if (null !== this.contentDivRef.current) {
            let height = this.contentDivRef.current.clientHeight;
            // Plus 2 - one to counteract Math.floor, one to cover the gap at the bottom.
            this.numVisibleRows = Math.floor(height / this._pixelsPerRow) + 1;
        } else {
            throw new Error();
        }
    }

    /* After rendering the page, put the focus back where it was. */
    private reSetFocus = () => {
        document.getElementById(keyOf(this.focusColumn, this.focusRow))?.focus();
    }

    /* After pageUp / pageDown, reload more data to scroll the page to the right place. */
    private scrollToFocus = () => {
        if (this.focusRow !== undefined) {
            if (this.focusRow < this.state.firstVisibleRow) {
                // Scroll up
                let newFirstVisibleRow = Math.max(0, Math.floor(this.focusRow - (this.numVisibleRows / 2)));
                this.setState({ firstVisibleRow: newFirstVisibleRow });
            } else if (this.focusRow >= this.state.firstVisibleRow + this.numVisibleRows) {
                // Scroll down
                let newFirstVisibleRow = Math.min(this.state.count - 1, Math.floor(this.focusRow - (this.numVisibleRows / 2)));
                this.setState({ firstVisibleRow: newFirstVisibleRow });
            }
        }
    }

    private fetchRows = () => {
        let query = this.props.query;
        let queryVersion = this.props.query.version;
        let depth = this.props.query.depth();
        let firstVisibleRow: number;
        if (depth > 1) {
            firstVisibleRow = this.state.firstVisibleRow / ((depth - 1) * Table.MAX_EXPANDED_ROWS);
        } else {
            firstVisibleRow = this.state.firstVisibleRow;
        }

        let lastRenderedRow = firstVisibleRow + this.numVisibleRows + 2;

        this.props.query
            .fetch(this.props.fetcher, firstVisibleRow, lastRenderedRow)
            .then(
                (rows: Row[]) => {
                    try {
                        if (query === this.props.query && queryVersion === this.props.query.version) { // if it's still the same query...
                            let expandedRows: Row[] = expandRows(this.props.query, rows);
                            this.considerResizingColumns(expandedRows);
                            this.setState({
                                visibleRows: new Loaded<Row[]>(expandedRows)
                            });
                        } else {
                            // This is benign. It just means that a response came out of order and the user 
                            // has now scrolled away making the data useless.
                            //console.log("Woops, too slow. Received rows for an old query.");
                        }
                    } catch (e) {
                        console.error(e);
                        this.setState({
                            visibleRows: new LoadError(new Failure(String(e), "Details in the console."))
                        });
                    }
                },
                error => {
                    console.log(error);
                    this.setState({
                        visibleRows: new LoadError(error)
                    });
                });
    }

    /** Quickly iterate through the data and see whether we can choose some decent column widths. */
    private considerResizingColumns = (rows: Row[]) => {
        this.considerResizingEachColumnIn(this.props.query, rows);
    }

    private readonly ESTIMATED_CHARACTER_WIDTH = 10; // Maybe use a ref to get an actual value?
    private readonly INITIAL_MAX_COLUMN_SIZE = this.ESTIMATED_CHARACTER_WIDTH * 80; // The user can make it bigger if they want.
    private considerResizingEachColumnIn = (column: QueryColumn, rows: Row[]) => {
        for (let each of column.columns) {
            if (each.isExpanded) {
                this.considerResizingEachColumnIn(each, rows);
            } else {
                if (!each.hasPixelWidth) {
                    let columnIndex = each.columnNumber;
                    let sizeSoFar = 3;
                    for (let i = 0; i < rows.length; i++) {
                        let cellContents = String(rows[i].cells[columnIndex]._value);
                        let proposedWidth = cellContents.length * this.ESTIMATED_CHARACTER_WIDTH;
                        if (proposedWidth > sizeSoFar) {
                            sizeSoFar = proposedWidth;
                        }
                        if (proposedWidth > this.INITIAL_MAX_COLUMN_SIZE) {
                            sizeSoFar = this.INITIAL_MAX_COLUMN_SIZE;
                            break;
                        }
                    }

                    let titleSize = Math.min(this.INITIAL_MAX_COLUMN_SIZE, (each.name.length+1) * this.ESTIMATED_CHARACTER_WIDTH);
                    each.pixelWidth = Math.max(titleSize, sizeSoFar);
                }
            }
        }
    }


    /** Return true if any of the columns does not currently have a size. */
    private columnsAreWorthResizing = (columns: QueryColumn[]) => {
        for (let each of columns) {
            if (each.isExpanded && this.columnsAreWorthResizing(each.columns)) {
                return true;
            } else {
                return null === each.pixelWidth;
            }
        }
    }

    private fetchCount = () => {
        this.props.query.getCount(this.props.fetcher).then(n => {
            this.setState({ count: n })
        });
    }

    /* Move the column from the drag&drop text to the given pixel position.
     
    We're handing dragging and dropping a column from a hierarchy to elsewhere in 
    a hierarchy. I've decided on these rules:
    * You can move any column in the hierarchy to the root, but if it wasn't 
      originally in the root then you are prompted to rename it.
    * You can reorder columns if they're in the same branch.
    * You can remove columns entirely by dragging them away from the hierarchy.
      (Failing that, maybe I'll add a rubbish bin drop target.)
    */

    onHeadingDrop = (ev: React.DragEvent<HTMLDivElement>) => {
        // Don't forget about the potential of drag/dropping columns between multiple tables.
        ev.preventDefault();

        let from: QueryColumn | null = this.getColumnFromDragData(ev.dataTransfer);
        if (!from) {
            return;
        }
        let x: number | null = this.datatablePos(ev.clientX);
        if (!x) {
            return;
        }

        let to: number = this.findExpandedColumnIndexAtPixelX(x);
        let q: Query = this.props.query;
        q.moveColumn(from, to);
        this.setState({ dropColumnMarkerPosition: null });
    }

    // DragEventHandler<HTMLDivElement> | undefined;
    onHeadingDragOver = (ev: React.DragEvent<HTMLDivElement>) => {
        ev.preventDefault();

        /* A few issues with the DND API:
            1. Only Firefox lets you see the data, so we need to use the
               type to communicate the column name.
            2. It gets converted to lower case. 
        */
        let from: QueryColumn | null = this.getColumnFromDragData(ev.dataTransfer);
        if (!from) {
            return;
        }

        let x: number | null = this.datatablePos(ev.clientX);
        if (!x) {
            return;
        }

        let [pos, to] = this.snapToColumnEdge(x);

        if (this.canBeMovedNextTo(from, to)) {
            this.setState({ dropColumnMarkerPosition: pos });
        } else {
            console.log("Cannot be moved.")
        }
    }

    private findExpandedColumnIndexAtPixelX = (pixelPosition: number) => {
        // -1 means insert before the first column.
        let index = -1;
        // Start at half a column's width to the left of the first column.
        let widthSoFar = 0;
        for (let c of this.props.query.expandedColumns) {
            if (widthSoFar + c.pixelWidth / 2 > pixelPosition) {
                return index;
            }
            widthSoFar = widthSoFar + c.pixelWidth;
            index++;
        }

        return index;
    }

    private getColumnFromDragData = (data: DataTransfer) => {
        for (let i = 0; i < data.items.length; i++) {
            let dragData = data.items[i].type;
            if (dragData.startsWith(RowHeader.DragDataPrefix)) {
                let columnName = dragData.substring(RowHeader.DragDataPrefix.length).toLowerCase();
                for (let c of this.props.query.expandedColumns) {
                    if (c.textualId.toLowerCase() === columnName) {
                        return c;
                    }
                }
            }
        }
        return null;
    }

    /* Return the position of the event relative to the left side of the datatable. */
    private datatablePos = (clientX: number) => {
        let headerLeft = this.headerDivRef.current?.getBoundingClientRect().left;
        if (headerLeft) {
            return clientX - headerLeft + this.state.columnScroll;
        } else {
            return null;
        }
    }

    private snapToColumnEdge(x: number): [number, QueryColumn] {
        let widthSoFar = 0;
        for (let c of this.props.query.expandedColumns) {
            if (widthSoFar > x - c.pixelWidth / 2) {
                return [widthSoFar, c];
            }
            widthSoFar = widthSoFar + c.pixelWidth;
        }
        return [widthSoFar, this.props.query.expandedColumns[this.props.query.expandedColumns.length - 1]];
    }

    /* Return true if the currently dragged column (from) can be moved to the position before the drop target (to). */
    private canBeMovedNextTo(from: QueryColumn, to: QueryColumn) {
        // I'd like to unify this code with moveColumn. This is just a "dry run" of moveColumn.
        let targetIsRootLevel = to._parent === this.props.query;
        let canMoveToRoot = targetIsRootLevel || this.isFirstInExpandedColumn(to);

        let hasSharedParent = to._parent === from._parent;

        return canMoveToRoot
            || hasSharedParent;
    }

    private isFirstInExpandedColumn(q: QueryColumn | null): boolean {
        if (q?._parent?._basedOn?.type === ColumnType.RootColumn) return true;
        let family = q?._parent?.columns;
        if (null === family || undefined === family) {
            return false;
        }
        if (q === family[0]) {
            if (q._parent === undefined) { // make the typescript compiler happy.
                return true;
            }
            return this.isFirstInExpandedColumn(q._parent);
        } else {
            return false;
        }
    }

    /* For resizing columns */
    onMouseMove: MouseEventHandler<HTMLDivElement> = (ev) => {
        if (this.state.resizingColumn) {
            ev.preventDefault(); // Stop us from selecting text.

            /* 
    
            +---------Page----------+
            |   +------DataTable----|
            |   |olumn | column | co|
              (|column | ...)         <--- show left edge of header while scrolling.
                             ^ mouse pointer is here. This is how wide 'column' should be.
            ----               = headerLeft
               -------         = columnLeft 
            -----------------  = ev.clientX
               --------------  = ev.clientX - headerLeft
                       ------  = ev.clientX - headerLeft - columnLeft
    
            */

            // headerLeft is the coordinate of the left side of the header.
            // even when it's scrolled.
            let headerLeft = this.headerDivRef.current?.getBoundingClientRect().left;
            if (!headerLeft) {
                return null;
            }

            let columnLeft = this.props.query.expandedColumns
                .slice(0, this.state.resizingColumn.columnNumber)
                .map(each => each.pixelWidth)
                .reduce((x, y) => x + y, 0);

            let columnWidth = ev.clientX - headerLeft - columnLeft;

            // TODO: this is bad React practise.
            // eslint-disable-next-line
            this.state.resizingColumn.pixelWidth = columnWidth;
            this.setState(this.state);
        }
    }

    onMouseDown = (ev: React.MouseEvent<HTMLDivElement>, column: QueryColumn) => {
        ev.preventDefault(); // Stop us from selecting text instead.
        this.setState({ resizingColumn: column });
    }

    onMouseUp: MouseEventHandler<HTMLDivElement> = (ev) => {
        this.setState({ resizingColumn: undefined });
    }

    onFilterChanged = () => {
        this.props.query.changed(); // Makes this.componentDidUpdate() do stuff.
        this.setState(this.state);
    }

    // TODO: am I called in the render() method? In that case, should I not get a new 
    // instance for every render?
    rowHeaderContextMenu = (target: QueryColumn | undefined) => {
        if (!target) return []; // TODO: disable the actions so they're greyed out in the menu.
        let result: Action[] = [];
        result.push(new ShowColumn(this, target));
        result.push(new HideColumn(this, target));

        for (let each of this.props.menu) {
            result.push(each);
        }

        return result;
    }


    onRowHeaderContextMenu = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>, column: QueryColumn) => {
        this.setState({
            rowHeaderContextMenuVisible: true,
            cellContextMenuVisible: false,
            contextMenuTop: ev.pageY,
            contextMenuLeft: ev.pageX,
            rowHeaderSelectedColumn: column
        });
    }

    onCellContextMenu: MouseEventHandler<HTMLDivElement> = (ev) => {
        // TODO: selectedCell?
        ev.preventDefault();

        this.setState({
            cellContextMenuVisible: true,
            rowHeaderContextMenuVisible: false,
            contextMenuTop: ev.pageY,
            contextMenuLeft: ev.pageX
        });
    }

    selectColumnMenu = () => {
        let result: Action[] = [];
        let _this = this;
        for (let each of this.props.query.table.columns.children) {
            result.push(new class extends Action {
                label = each.name;
                do = () => {
                    _this.addColumnAfter(each, undefined);
                }
            }());
        }
        return result;
    };

    showColumnMenu = (after: QueryColumn) => {
        this.setState({ selectColumnMenuVisible: true });
    }

    addColumnAfter(c: TableColumn, after: QueryColumn | undefined) {
        // TODO: insert where the pointer is.
        // TODO: we should be asking the Query to do this for us.
        this.props.query.addColumnByName(c.name);
        this.dismissContextMenu();
    }

    removeColumn(target: any) {
        this.props.query.removeColumn(target);
        this.dismissContextMenu();
    }
}

interface PositionedHeading {
    columnStart: number;
    columnEnd: number;
    row: number;
    columnDefinition: QueryColumn;
}

/* I'm a processed copy of the columns to make rendering easier. Instead of a tree, I'm 
organised by row of columns. 

To be honest, I'm really just a 2D array.
*/
class ColumnsLaidOut {
    private columnRows: Array<Array<QueryColumn | undefined>> = [];

    static fromColumnDefinition(root: MultipleQueryColumn): ColumnsLaidOut {
        let result: ColumnsLaidOut = new ColumnsLaidOut();
        let rightEdge: number[] = [0]; // I'm a container for a mutable value.
        this.fromColumnDefinitionImpl(root, 0, rightEdge, result);
        return result;
    }

    static fromColumnDefinitionImpl(
        column: MultipleQueryColumn,
        depth: number,
        rightEdge: number[],
        result: ColumnsLaidOut) {
        for (let each of column.columns) {
            result.put(rightEdge[0], depth, each);
            if (each instanceof MultipleQueryColumn && each.isExpanded) {
                this.fromColumnDefinitionImpl(each, depth + 1, rightEdge, result);
            } else {
                rightEdge[0] = rightEdge[0] + 1;
            }
        }
    }

    /* Put that object at x,y. I'm effectively a 2D array. Pad with undefineds when necessary. */
    put = (x: number, y: number, me: QueryColumn) => {
        while (this.columnRows.length <= y) {
            this.columnRows.push([]);
        }
        let myRow = this.columnRows[y];
        while (myRow.length <= x) {
            myRow.push(undefined);
        }
        myRow[x] = me;
    }

    /* Map a function, passing a PositionedHeading in. */
    map<U>(
        callbackfn: (value: PositionedHeading, index: number, array: PositionedHeading[]) => U,
        thisArg?: any)
        : U[] {
        let result: U[] = [];
        let y: number = 0;
        for (let rowIter of this.columnRows) {
            // This code is funky because I'm having compiler issues.
            let row: Array<QueryColumn | undefined> = rowIter;
            for (let x: number = 0; x < row.length; x++) {
                let r: QueryColumn;
                if (undefined !== row[x]) {
                    r = row[x] as QueryColumn;
                } else {
                    continue;
                }

                // The width of a row includes all the "undefined" columns after it.
                let thisWidth = 0;
                while (1 + x + thisWidth < row.length && undefined === row[1 + x + thisWidth]) {
                    thisWidth++;
                }

                let value = {
                    columnStart: x,
                    columnEnd: x + thisWidth,
                    row: y,
                    columnDefinition: r
                };
                let resultValue = callbackfn(value, x, [value]);
                result.push(resultValue);
            }
            y++;
        }
        return result;
    }

    get = (x: number, y: number) => {
        return this.columnRows[y][x];
    }
}

function keyOf(x?: number, y?: number) {
    return `R${y}C${x}`;
}


class ShowColumn extends Action {
    label = "Show column";
    description = "Show a column that is not currently visible";

    private callback: DataTable;
    target: QueryColumn;

    constructor(callback: DataTable, target: QueryColumn) {
        super();
        this.callback = callback;
        this.target = target;
    }

    do(p?: ProgressNotifier | undefined): void {
        this.callback.showColumnMenu(this.target);
    }

}

class HideColumn extends Action {
    label = "Hide column";
    description = "Hide the currently selected column";

    private callback: DataTable;
    target: QueryColumn;

    constructor(callback: DataTable, target: QueryColumn) {
        super();
        this.callback = callback;
        this.target = target;
    }

    do(p?: ProgressNotifier | undefined): void {
        this.callback.removeColumn(this.target);
    }

}

class CopyURL extends Action {
    label = "Copy OData query URL";
    description = 'Copy the OData URL used in this query to the clipboard';
    private callback: DataTable;

    constructor(callback: DataTable) {
        super();
        this.callback = callback;
    }

    do(p?: ProgressNotifier | undefined): void {
        this.callback.onCopyUrl()
    }

}

/** Return how that column in the query is ordered. */
function orderedBy(table: MultipleQueryColumn, column: QueryColumn): OrderedBy {
    let possibleResult = table.getOrderBy().find(
        each => each.column.name === column.name);
    if (possibleResult) {
        return possibleResult.orderedBy;
    } else {
        return OrderedBy.NA;
    }
}